import { Icon } from '@plone/volto/components';
import paintBucketSVG from '@plone/volto/icons/paint.svg';
import heroSVG from '@plone/volto/icons/hero.svg';
import cardsSVG from '@plone/volto/icons/cards.svg';
import iconSVG from '@plone/volto/icons/select-all.svg';
import sliderSVG from '@plone/volto/icons/slider.svg';
import calendarSVG from '@plone/volto/icons/calendar.svg';
import { buildRestriction } from './config/blockRestrictions';
import blockThemes from './config/blockThemes';
import { injectEditThemeStyles } from './utils/injectEditThemeStyles';
import { Edit as CustomHeroEdit, View as CustomHeroView} from './components/Blocks/CustomHero';
import { Edit as ButtonRowEdit, View as ButtonRowView} from './components/Blocks/ButtonRow';
import { Edit as MultiCardEdit, View as MultiCardView } from './components/Blocks/MultiCardBlock';
import { Edit as IconLinkRowEdit, View as IconLinkRowView} from './components/Blocks/IconLinkRow';
import { Edit as EmblaCarouselEdit, View as EmblaCarouselView} from './components/Blocks/EmblaCarousel';
import { Edit as EventsEdit, View as EventsView } from './components/Blocks/Events';
import JuiziEventMetaData from './components/Blocks/JuiziEventMetaData/View';
import SocialSharing from '@codesyntax/volto-social-sharing/SocialSharing';
import { DEFAULT_SOCIAL } from '@codesyntax/volto-social-sharing/defaultSettings';
import { faSquareXTwitter } from '@fortawesome/free-brands-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';

library.add(faSquareXTwitter);

//Custom View imports
import FileView from './components/Views/File';
export { FileView };
import EventView from './components/Views/EventView';
export { EventView };

const applyConfig = (config) => {
  config.settings.isMultilingual = false;
  config.settings.supportedLanguages = ['en'];
  config.settings.defaultLanguage = 'en';
  
  config.blocks.themes = blockThemes;

  if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        () => injectEditThemeStyles(blockThemes),
        { once: true },
      );
    } else {
      injectEditThemeStyles(blockThemes);
    }
  }

  //Add additional volto-columns-block colours here
  config.blocks.blocksConfig.columnsBlock.available_colors = [
    '#EFEFEF',
    '#EB9694',
    '#FAD0C3',
    '#FEF3BD',
    '#C1E1C5',
    '#BEDADC',
    '#C4DEF6',
    '#BED3F3',
    '#D4C4FB',
  ];

  //Enable autoplay on Slider
  config.blocks.blocksConfig.slider.enableAutoPlay = true;

  //Adding custom colours and block styles to Slate editor
  config.settings.slate.styleMenu.inlineStyles = [
    ...(config.settings.slate.styleMenu?.inlineStyles || []),
    {
      cssClass: 'text-red',
      label: 'Red Text',
      icon: () => <Icon name={paintBucketSVG} size="18px" color="red" />,
    },
    {
      cssClass: 'text-green',
      label: 'Green Text',
      icon: () => <Icon name={paintBucketSVG} size="18px" color="green" />,
    },
    {
      cssClass: 'text-blue',
      label: 'Blue Text',
      icon: () => <Icon name={paintBucketSVG} size="18px" color="blue" />,
    },
  ];
  config.settings.slate.styleMenu.blockStyles = [
    ...(config.settings.slate.styleMenu?.blockStyles || []),
    {
      cssClass: 'highlight-block',
      label: 'Highlighted Block',
      nodeType: 'paragraph', // Ensures it only applies to paragraph blocks
      icon: () => (
        <div
          style={{
            backgroundColor: 'yellow',
            width: '18px',
            height: '18px',
            borderRadius: '3px',
          }}
        />
      ),
    },
  ];

  //CustomHero
  config.blocks.blocksConfig.customHero = {
    id: 'customHero',
    title: 'Hero Block',
    icon: heroSVG,
    group: 'common',
    view: CustomHeroView,
    edit: CustomHeroEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //ButtonRow
  config.blocks.blocksConfig.buttonRow = {
    id: 'buttonRow',
    title: 'Button Row',
    icon: cardsSVG,
    group: 'common',
    view: ButtonRowView,
    edit: ButtonRowEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //add in social sharing block
  /*config.settings.appExtras = [
    ...config.settings.appExtras,
    {
      match: '',
      component: SocialSharing,
      props: {
        socialElements: DEFAULT_SOCIAL,
      },
    },
  ];*/

  //IconLinkRow
  config.blocks.blocksConfig.iconLinkRow = {
    id: 'iconLinkRow',
    title: 'Icon Link Row',
    icon: iconSVG,
    group: 'common',
    view: IconLinkRowView,
    edit: IconLinkRowEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //Multicard Block
  config.blocks.blocksConfig.multiCard = {
    id: 'multiCard',
    title: 'Multi Card',
    icon: cardsSVG,
    group: 'common',
    view: MultiCardView,
    edit: MultiCardEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //Embla Carousel
  config.blocks.blocksConfig.EmblaCarousel = {
    id: 'EmblaCarousel',
    title: 'Embla Carousel',
    icon: sliderSVG,
    group: 'common',
    view: EmblaCarouselView,
    edit: EmblaCarouselEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //Events block for listing and carousel
  config.blocks.blocksConfig.Events = {
    id: 'Events',
    title: 'Events',
    icon: calendarSVG,
    group: 'common',
    view: EventsView,
    edit: EventsEdit,
    restricted: false,
    mostUsed: true,
    sidebarTab: 1,
  };

  //Custom Event Meta Data
  const b = config.blocks.blocksConfig;
  const EVENT_META_ID = 'eventMetadata'; // replace with the real @type if different

  if (b[EVENT_META_ID]) {
    b[EVENT_META_ID] = {
      ...b[EVENT_META_ID],
      view: JuiziEventMetaData,
      edit: JuiziEventMetaData,
    };
  } else {
    console.warn(
      `Event metadata block "${EVENT_META_ID}" not found in blocksConfig. Available ids:`,
      Object.keys(b)
    );
  }

  //Custom views
  config.views = {
    ...config.views,
    contentTypesViews: {
      ...(config.views?.contentTypesViews || {}),
      File: FileView,
      Event: EventView,
    },
  };

  //Configure which blocks show and for which Editors (Keep last in line, after block registrations)
  const blocksConfig = config.blocks.blocksConfig;
  Object.keys(blocksConfig).forEach((blockId) => {
    const originalRestricted = blocksConfig[blockId].restricted;
    blocksConfig[blockId] = {
      ...blocksConfig[blockId],
      restricted: buildRestriction(blockId, originalRestricted),
    };
  });


//Volto Social Sharing
  DEFAULT_SOCIAL.push({
    name: "LinkedIn",
    fa_name: ["fab", "linkedin-in"],
    color: "#0a66c2",
    sharing_url: "https://www.linkedin.com/sharing/share-offsite/?url=",
    id: "li"
  });
  let twitter = DEFAULT_SOCIAL.find((social) => social.id === 'tw');
  if (twitter) {
    twitter.fa_name = faSquareXTwitter;
    twitter.color = 'black';
    twitter.sharing_url = "https://x.com/intent/post?text=Check%20this%20out:&url=";
;
  }
  config.settings.appExtras = [
    ...config.settings.appExtras,
    {
      match: {
        path: ['**/news/**', '**/events/**', '**/engagement/**'],
        exact: false,
      },
      component: SocialSharing,
      props: {
        socialElements: DEFAULT_SOCIAL,
      },
    },
  ];


  return config;
};

export default applyConfig;
